import {
  isToolPart,
  mergeDuplicateToolParts,
  partsFromLangGraphMessage,
} from './BidBoostLangGraphTransport'

function timeValue(value) {
  if (value == null) return null
  const result = typeof value === 'number' ? value : Date.parse(value)
  return Number.isFinite(result) ? result : null
}

function recordMessage(record) {
  if (!record?.message || !['assistant', 'tool'].includes(record.kind))
    return null
  return {
    id: record.message.id || record.id,
    role: 'assistant',
    parts: partsFromLangGraphMessage(record.message),
    metadata: {
      agent: {
        sourceMessageId: record.message.id || record.id,
        turnId: record.turn_id,
        source: record.source || 'root',
        kind: record.kind,
        parentToolCallId: record.parent_tool_call_id,
        startedAt: record.started_at,
        endedAt: record.ended_at,
      },
    },
  }
}

function agentMetadata(message) {
  const value = message.metadata?.agent || {}
  return {
    ...value,
    turnId: value.turnId || value.turn_id,
    parentToolCallId: value.parentToolCallId || value.parent_tool_call_id,
    source: value.source || (value.namespace ? 'subagent' : 'root'),
  }
}

function sourceMessageIds(message) {
  const agent = agentMetadata(message)
  return new Set(
    [
      message.id,
      agent.sourceMessageId,
      ...(agent.sourceMessageIds || []),
    ].filter(Boolean)
  )
}

function sameSourceMessage(left, right) {
  const ids = sourceMessageIds(left)
  return [...sourceMessageIds(right)].some((id) => ids.has(id))
}

function isRootMessage(message) {
  const agent = agentMetadata(message)
  return agent.source === 'root' && !agent.parentToolCallId
}

function hasAnswerContent(message) {
  return (
    message.attachments?.length ||
    message.parts?.some(
      (part) =>
        (part.type === 'text' && part.text?.trim()) ||
        ['file', 'source-url', 'source-document'].includes(part.type)
    )
  )
}

/** Union concurrent operations so parallel subagents do not inflate elapsed time. */
export function activityDurationMs(
  records = [],
  { now = Date.now(), running = false } = {}
) {
  const intervals = records
    .filter(
      (record) =>
        ['assistant', 'tool', 'timing'].includes(record.kind) &&
        !record.exclude_from_duration &&
        !(record.kind === 'tool' && record.message?.name === 'task')
    )
    .map((record) => [
      timeValue(record.started_at),
      timeValue(record.ended_at) ?? (running ? now : null),
    ])
    .filter(([start, end]) => start !== null && end !== null && end >= start)
    .sort((a, b) => a[0] - b[0])
  if (!intervals.length) return null
  let total = 0
  let [start, end] = intervals[0]
  for (const [nextStart, nextEnd] of intervals.slice(1)) {
    if (nextStart <= end) end = Math.max(end, nextEnd)
    else {
      total += end - start
      start = nextStart
      end = nextEnd
    }
  }
  return total + end - start
}

function turnSources(turn, ledgerRecords) {
  const sources = new Map()
  const toolStates = new Map(
    mergeDuplicateToolParts(
      turn.messages.flatMap((message) => message.parts || []).filter(isToolPart)
    ).map((part) => [part.toolCallId, part])
  )
  const withToolResults = (message) => ({
    ...message,
    parts: message.parts.map((part) => {
      const existing = isToolPart(part) && toolStates.get(part.toolCallId)
      return existing ? mergeDuplicateToolParts([existing, part])[0] : part
    }),
  })
  for (const message of turn.messages) {
    const streamed = (message.parts || []).filter(
      (part) => part.type === 'data-agent-message'
    )
    if (streamed.length) {
      for (const part of streamed) {
        const source = part.data
        if (source?.id) sources.set(source.id, source)
      }
    } else {
      const parts = (message.parts || []).filter(
        (part) => !part.type?.startsWith('data-')
      )
      if (parts.length || message.attachments?.length)
        sources.set(message.id, { ...message, parts })
    }
  }
  const checkpointMessages = []
  for (const record of ledgerRecords) {
    const message = recordMessage(record)
    if (message) {
      const existing = [...sources.values()].find((source) =>
        sameSourceMessage(source, message)
      )
      if (existing) {
        // Keep the live component key stable when a provider's final message
        // ID differs from its streamed LangChain ID.
        message.metadata.agent.sourceMessageIds = [
          ...new Set([
            ...sourceMessageIds(existing),
            ...sourceMessageIds(message),
          ]),
        ]
        message.id = existing.id
      }
      if (existing?.attachments?.length)
        message.attachments = existing.attachments
      const attachments =
        existing?.parts?.filter((part) =>
          ['file', 'source-url', 'source-document'].includes(part.type)
        ) || []
      if (attachments.length) message.parts.push(...attachments)
      checkpointMessages.push(message)
    }
  }
  if (!checkpointMessages.length)
    return [...sources.values()].map(withToolResults)
  const ordered = [...sources.values()]
  let insertionIndex = ordered.length
  // Shared IDs anchor the two sequences: the ledger contains child records
  // absent from root history, and live messages can be newer than checkpoints.
  for (const message of [...checkpointMessages].reverse()) {
    const existingIndex = ordered.findIndex((existing) =>
      sameSourceMessage(existing, message)
    )
    if (existingIndex !== -1) {
      ordered[existingIndex] = message
      insertionIndex = existingIndex
    } else {
      ordered.splice(insertionIndex, 0, message)
    }
  }
  return ordered.map(withToolResults)
}

function activityPartsForSources(sources, finalMessage) {
  const parts = []
  for (const message of sources) {
    const agent = agentMetadata(message)
    for (const [index, part] of (message.parts || []).entries()) {
      if (part.type.startsWith('data-') || part.type === 'step-start') continue
      if (
        message.id === finalMessage?.id &&
        ['text', 'file', 'source-url', 'source-document'].includes(part.type)
      )
        continue
      if (part.type === 'text' && !part.text?.trim()) continue
      if (
        part.type === 'reasoning' &&
        !part.text?.trim() &&
        !part.title?.trim()
      )
        continue
      parts.push({
        ...part,
        id: part.toolCallId || part.id || `${message.id}:${index}`,
        sourceMessageId: message.id,
        parentToolCallId: agent.parentToolCallId,
        namespace: agent.namespace,
      })
    }
  }

  const merged = mergeDuplicateToolParts(parts)
  const tools = new Map(
    merged.filter(isToolPart).map((part) => [part.toolCallId, part])
  )
  const rootParts = []
  for (const part of merged) {
    const parent = part.parentToolCallId && tools.get(part.parentToolCallId)
    if (parent && parent !== part) {
      parent.children ||= []
      parent.children.push(part)
    } else {
      rootParts.push(part)
    }
  }
  return rootParts
}

/**
 * One user message plus all resumed assistant runs is a single display turn.
 * Source messages and the persisted activity ledger remain separate from the
 * AI SDK's transport message, which is allowed to flatten many model calls.
 */
export function buildAgentTurns(messages = [], options = {}) {
  const {
    now = Date.now(),
    isRunning = false,
    pendingInterrupt = null,
    error = null,
    stopped = false,
  } = options
  const turns = []
  const records = new Map()
  const snapshots = new Map()
  for (const message of messages) {
    for (const part of message.parts || []) {
      if (part.type === 'data-agent-ledger' && part.data?.id)
        records.set(part.data.id, part.data)
      if (part.type === 'data-agent-turn' && part.data?.id)
        snapshots.set(part.data.id, part.data)
    }
    if (message.role === 'user') {
      turns.push({ id: message.id, userMessage: message, messages: [] })
    } else if (message.role === 'assistant') {
      if (!turns.length)
        turns.push({
          id: `turn:${message.id}`,
          userMessage: null,
          messages: [],
        })
      turns.at(-1).messages.push(message)
    }
  }

  return turns.map((turn, index) => {
    const latest = index === turns.length - 1
    const snapshot = snapshots.get(turn.id)
    const ledger = [...records.values()].filter(
      (record) => record.turn_id === turn.id
    )
    const persistedStatus = ledger.findLast(
      (record) => record.kind === 'status'
    )?.status
    let status =
      snapshot?.status ||
      {
        completed: 'complete',
        running: 'stopped',
        error: 'error',
        stopped: 'stopped',
        waiting: 'waiting',
      }[persistedStatus] ||
      'complete'
    if (latest) {
      if (pendingInterrupt) status = 'waiting'
      else if (stopped) status = 'stopped'
      else if (error) status = 'error'
      else if (isRunning) status = 'running'
      else if (status === 'running') status = 'stopped'
    } else if (status === 'running') status = 'stopped'

    const sources = turnSources(turn, ledger)
    const rootSources = sources.filter(
      (message) =>
        isRootMessage(message) && agentMetadata(message).kind !== 'tool'
    )
    const lastRoot = rootSources.at(-1)
    const answer =
      lastRoot && hasAnswerContent(lastRoot) && !lastRoot.parts.some(isToolPart)
        ? lastRoot
        : null
    const finalMessage = answer
      ? {
          ...answer,
          parts: answer.parts.filter((part) =>
            ['text', 'file', 'source-url', 'source-document'].includes(
              part.type
            )
          ),
        }
      : null

    // Live segments use the client clock for a smooth timer. Checkpointed
    // intervals become authoritative on completion and when reopening a chat.
    const persistedDuration = activityDurationMs(ledger, {
      now,
      running: status === 'running',
    })
    const segmentStart = timeValue(snapshot?.segmentStartedAt)
    const previousDuration = activityDurationMs(
      ledger.filter((record) => {
        const end = timeValue(record.ended_at)
        return end !== null && segmentStart !== null && end <= segmentStart
      })
    )
    const liveDuration =
      snapshot && !(status !== 'running' && snapshot.status === 'running')
        ? Math.max(snapshot.durationMs || 0, previousDuration || 0) +
          (status === 'running' && segmentStart !== null
            ? Math.max(0, now - segmentStart)
            : 0)
        : null
    const durationMs =
      status === 'running'
        ? (liveDuration ?? persistedDuration)
        : (persistedDuration ?? liveDuration)

    return {
      ...turn,
      finalMessage,
      activityParts: activityPartsForSources(sources, finalMessage),
      status,
      durationMs,
    }
  })
}
